import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { STATUS_META, PART_STATUS_META, PART_STATUSES } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Package, ArrowRight, CheckCircle2, RefreshCw } from "lucide-react";

export default function PartsToOrder() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/parts-to-order");
      setGroups(data);
    } catch (e) {
      toast.error("Failed to load parts queue");
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setLineStatus = async (order_id, part_index, status) => {
    try {
      await api.patch(`/work-orders/${order_id}/parts/${part_index}/status`, {
        status,
      });
      toast.success(`Marked ${PART_STATUS_META[status].label}`);
      await load();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const markAllReceivedInGroup = async (group) => {
    for (const o of group.orders) {
      try {
        await api.patch(
          `/work-orders/${o.order_id}/parts/${o.part_index}/status`,
          { status: "received" }
        );
      } catch (e) {
        /* silent */
      }
    }
    toast.success(`${group.name} — all received`);
    await load();
  };

  const totalPending = groups.reduce((s, g) => s + (g.pending_quantity || 0), 0);
  const totalOnOrder = groups.reduce((s, g) => s + (g.ordered_quantity || 0), 0);

  return (
    <div>
      <PageHeader
        testid="parts-to-order-header"
        title="Parts to Order"
        subtitle="Track every part from pending to received."
        actions={
          <Button
            variant="outline"
            onClick={load}
            className="rounded-xl border-2 border-warm"
            data-testid="pto-refresh"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Card
          className="p-5 rounded-2xl border-warm shadow-card bg-white"
          data-testid="pto-summary-parts"
        >
          <div className="text-xs uppercase tracking-widest text-slate-muted font-semibold mb-1">
            Distinct Parts
          </div>
          <div className="font-heading text-3xl font-bold text-[#2B2D42]">
            {loading ? "—" : groups.length}
          </div>
        </Card>
        <Card
          className="p-5 rounded-2xl border-warm shadow-card bg-white"
          data-testid="pto-summary-pending"
        >
          <div className="text-xs uppercase tracking-widest text-[#8B6914] font-semibold mb-1">
            Pending Units
          </div>
          <div className="font-heading text-3xl font-bold text-[#E5A82E]">
            {loading ? "—" : totalPending}
          </div>
        </Card>
        <Card
          className="p-5 rounded-2xl border-warm shadow-card bg-white"
          data-testid="pto-summary-ordered"
        >
          <div className="text-xs uppercase tracking-widest text-[#3D5A6C] font-semibold mb-1">
            On Order Units
          </div>
          <div className="font-heading text-3xl font-bold text-[#3D5A6C]">
            {loading ? "—" : totalOnOrder}
          </div>
        </Card>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-muted rounded-2xl border-warm">
          Loading parts queue…
        </Card>
      ) : groups.length === 0 ? (
        <EmptyState
          testid="pto-empty"
          image="https://images.unsplash.com/photo-1702146715471-ae6b10689969?crop=entropy&cs=srgb&fm=jpg&w=400&q=70"
          title="All caught up"
          description="No parts pending — every open repair order has its parts received."
          action={
            <Link to="/work-orders">
              <Button
                className="bg-[#1A1A1A] hover:bg-[#2D2D2D] text-white rounded-xl"
                data-testid="pto-back-orders"
              >
                Back to Repair Orders
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-4" data-testid="parts-to-order-list">
          {groups.map((g) => {
            const groupKey = g.name.replace(/\s+/g, "-").toLowerCase();
            return (
              <Card
                key={groupKey}
                className="rounded-2xl border-warm shadow-card overflow-hidden bg-white"
                data-testid={`pto-group-${groupKey}`}
              >
                {/* Group header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 bg-[#1A1A1A] text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E5A82E]/20 flex items-center justify-center text-[#E5A82E]">
                      <Package className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-heading text-lg font-semibold leading-tight">
                        {g.name}
                      </div>
                      <div className="text-xs text-white/60 uppercase tracking-widest">
                        {g.pending_quantity > 0 && (
                          <span className="text-[#E5A82E]">
                            {g.pending_quantity} pending
                          </span>
                        )}
                        {g.pending_quantity > 0 && g.ordered_quantity > 0 && " · "}
                        {g.ordered_quantity > 0 && (
                          <span className="text-[#8AB4C8]">
                            {g.ordered_quantity} on order
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className="font-mono text-sm font-semibold bg-[#E5A82E]/20 border border-[#E5A82E]/40 text-[#E5A82E] rounded-full px-3 py-1"
                      data-testid={`pto-group-total-${groupKey}`}
                    >
                      {g.total_quantity} unit{g.total_quantity === 1 ? "" : "s"}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => markAllReceivedInGroup(g)}
                      className="bg-[#E5A82E] hover:bg-[#C8912A] text-[#1A1A1A] rounded-lg font-medium"
                      data-testid={`pto-mark-all-${groupKey}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> All received
                    </Button>
                  </div>
                </div>

                {/* Order lines */}
                <div className="divide-y divide-warm">
                  {g.orders.map((o) => {
                    const woMeta = STATUS_META[o.status] || STATUS_META.new;
                    const lineMeta =
                      PART_STATUS_META[o.line_status] || PART_STATUS_META.pending;
                    const k = `${o.order_number}-${o.part_index}`;
                    return (
                      <div
                        key={k}
                        className="flex flex-col md:flex-row md:items-center gap-3 px-6 py-3 hover:bg-[#FAF9F6]"
                        data-testid={`pto-line-${k}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {o.vehicle_plate ? (
                              <span className="font-mono text-sm font-semibold text-[#1A1A1A] bg-[#F4D35E]/30 border border-[#E5A82E]/40 rounded-md px-2 py-0.5">
                                {o.vehicle_plate.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-muted italic">no plate</span>
                            )}
                            <span className="font-mono text-xs text-slate-muted">
                              {o.order_number}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${woMeta.cls}`}
                            >
                              {woMeta.label}
                            </span>
                          </div>
                          <div className="text-xs text-slate-muted mt-0.5 truncate">
                            {o.vehicle_label || "Vehicle"}
                            {o.customer_name ? ` · ${o.customer_name}` : ""}
                          </div>
                        </div>
                        <div className="font-heading text-lg font-bold text-[#2B2D42] md:w-14 md:text-right">
                          × {o.quantity}
                        </div>
                        <Select
                          value={o.line_status}
                          onValueChange={(val) =>
                            setLineStatus(o.order_id, o.part_index, val)
                          }
                        >
                          <SelectTrigger
                            data-testid={`pto-line-status-${k}`}
                            className={`h-auto py-1 px-2.5 rounded-full text-xs font-semibold uppercase tracking-wider border w-auto gap-1.5 focus:ring-2 focus:ring-[#E5A82E] [&>svg]:h-3 [&>svg]:w-3 [&>svg]:opacity-70 ${lineMeta.cls}`}
                          >
                            <span>{lineMeta.label}</span>
                          </SelectTrigger>
                          <SelectContent className="rounded-xl">
                            {PART_STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Link
                          to={`/work-orders/${o.order_id}`}
                          data-testid={`pto-open-order-${o.order_number}`}
                        >
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-lg text-[#3D405B]"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
